import { toSocket } from "vscode-ws-jsonrpc";
import { IConnection, createServerProcess, createWebSocketConnection } from "vscode-ws-jsonrpc/server";
import WebSocket from "ws";
import { v4 as uuidv4 } from "uuid";
import { EventEmitter } from "events";
import { Message } from "vscode-jsonrpc";
import { transformClientToServer } from "../utils/pathTransformer.js";
import { transformServerToClient } from "../utils/pathTransformer.js";
import { ServerConfig } from "../types/server.js";
import { Logger } from "../utils/logger.js";
import { ValidatedConnection } from "../utils/validatedConnection.js";
import { disallowedFileExtensionValidator, workspaceValidator } from "../utils/uriValidators.js";

export function forward(clientConnection: IConnection, serverConnection: IConnection, map?: (message: Message, source: "client" | "server") => Message): void {
  clientConnection.forward(serverConnection, (message) => map?.(message, "client") ?? message);
  serverConnection.forward(clientConnection, (message) => map?.(message, "server") ?? message);
  clientConnection.onClose(() => serverConnection.dispose());
  serverConnection.onClose(() => clientConnection.dispose());
}


export interface Connection {
  id: string;
  wsConnection: IConnection;
  serverConnection: IConnection;
}

export class ConnectionManager extends EventEmitter {
  private connections: Map<string, Connection> = new Map();

  constructor(private readonly config: ServerConfig, private readonly logger: Logger) {
    super();
  }

  createConnection(websocket: WebSocket, languageServerPath: string): Connection {
    const connectionId = uuidv4();

    const wsConnection = this.createWebSocketConnection(websocket);
    if (!wsConnection) {
      throw new Error("Failed to create WebSocket connection");
    }

    const serverConnection = this.createServerConnection(languageServerPath);
    if (!serverConnection) {
      wsConnection.dispose();
      throw new Error("Failed to create server connection");
    }

    const connection = {
      id: connectionId,
      wsConnection,
      serverConnection,
    };
    this.logger.info(`Connection created: ${connectionId}`);

    this.connections.set(connectionId, connection);
    this.handleCloseConnections(connection);
    this.handleForwardMessages(connection);
    this.emit("connection:created", connectionId);

    return connection;
  }

  private createServerConnection(languageServerPath: string): IConnection | undefined {
    return createServerProcess("typescript-language-server", process.execPath, [languageServerPath, "--stdio", "--log-level", "4"], {
      stdio: ["pipe", "pipe", "pipe"],
      env: {
        ...process.env,
        NODE_ENV: "production",
      },
    });
  }

  private createWebSocketConnection(websocket: WebSocket): IConnection | undefined {
    const connection = createWebSocketConnection(toSocket(websocket));

    if (!this.config.workspacePathPrefix) {
      return connection;
    }

    return new ValidatedConnection(
      connection,
      [
        workspaceValidator(this.config.workspacePathPrefix),
        disallowedFileExtensionValidator([".env", ".env.local", ".env.development", ".env.production"]),
      ],
      this.logger
    );
  }

  private handleForwardMessages(connection: Connection): void {
    forward(connection.wsConnection, connection.serverConnection, (message, source) => {
      if (!this.config.workspacePathPrefix) {
        this.logMessage(message, source);
        return message;
      }

      const transformedMessage = source === "client"
        ? transformClientToServer(message, this.config.workspacePathPrefix)
        : transformServerToClient(message, this.config.workspacePathPrefix);

      this.logMessage(transformedMessage, source);

      return transformedMessage;
    });
  }

  private logMessage(message: Message, source: "client" | "server"): void {
    const logDirection = source === "client" ? "->LSP" : "<-LSP";
    if (Message.isRequest(message)) {
      this.logger.info(`${logDirection} - ${message.id} - Request: ${message.method}`);
    } else if (Message.isResponse(message)) {
      this.logger.info(`${logDirection} - ${message.id} - Response: ${message.result}`);
    } else if (Message.isNotification(message)) {
      this.logger.info(`${logDirection} - Notification: ${message.method}`);
    }
  }

  private handleCloseConnections(connection: Connection): void {
    connection.wsConnection.onClose(() => {
      this.logger.info(`WebSocket connection closed: ${connection.id}`);
      this.removeConnection(connection.id);
    });

    connection.serverConnection.onClose(() => {
      this.logger.info(`Language server connection closed: ${connection.id}`);
      this.removeConnection(connection.id);
    });
  }

  removeConnection(connectionId: string): void {
    const connection = this.connections.get(connectionId);
    if (connection) {
      connection.wsConnection.dispose();
      connection.serverConnection.dispose();
      this.connections.delete(connectionId);
      this.emit("connection:removed", connectionId);
    }
  }

  getConnection(connectionId: string): Connection | undefined {
    return this.connections.get(connectionId);
  }

  dispose(): void {
    for (const connection of this.connections.values()) {
      connection.wsConnection.dispose();
      connection.serverConnection.dispose();
    }
    this.connections.clear();
    this.emit("disposed");
  }
}
